import { useState, useEffect } from 'react';
import axios from 'axios';
import StudentSidebar from '../components/StudentSidebar.js';
import '../styles/StudentGrades.css'
import { BarChart2, Search } from 'lucide-react';
import { displayGrade, resolveRowGradeFormat } from '../utils/gradeValues';

export default function StudentGrades() {
    // State to hold modules
    const [modules, setModules] = useState([]);
    // State for selected modules
    const [selectedModule, setSelectedModule] = useState(null);
    // State to load modules
    const [loadModules, setLoadingModules] = useState(true);
    // State to search grades
    const [searchTerm, setSearchTerm] = useState('');
    // State to hold student stats
    const [studentStats, setStudentStats] = useState(null);
    // State to show student stats
    const [showStudentStats, setShowStudentStats] = useState(false);
    // State to load student stats
    const [loadingStudentStats, setLoadingStudentStats] = useState(false);
    // State to hold grades
    const [grades, setGrades] = useState([]);
    // State to load grades
    const [loadingGrades, setLoadingGrades] = useState(false);
    // State for modal
    const [selectedGradeDetails, setSelectedGradeDetails] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenModal = (grade) => {
        setSelectedGradeDetails(grade);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectedGradeDetails(null);
        setIsModalOpen(false);
    };

    const showGrade = (grade) => displayGrade(grade);

    const getGradeClass = (rate, row) => {
        const fmt = resolveRowGradeFormat(typeof row === 'object' ? row : { grade: rate });
        if (fmt === 'pass_fail') {
            const text = String(showGrade({ grade: rate, grade_format: fmt })).toLowerCase();
            return text.includes('zalicz') && !text.includes('nie') ? 'grade-green' : 'grade-red';
        }
        if (fmt === 'percent') {
            const n = Number(rate);
            if (n >= 90) return 'grade-green';
            if (n >= 60) return 'grade-yellow';
            return 'grade-red';
        }
        if (rate >= 4.5) return 'grade-green';
        if (rate >= 3.0) return 'grade-yellow';
        return 'grade-red';
    };

    const getStatusClass = (status) => {
        if (status === 'Zaliczony') return 'status-passed';
        return 'status-failed';
    }

    // Funkcja pomocnicza do sprawdzania czy wartość jest liczbą
    const isNumericGrade = (val) => {
        if (val === null || val === undefined || val === "") return false;
        const num = Number(val);
        return !isNaN(num) && isFinite(num);
    };

    // Funkcja do konwersji wartości na liczbę
    const toNumber = (val) => {
        if (val === null || val === undefined || val === "") return null;
        const num = Number(val);
        return isNaN(num) || !isFinite(num) ? null : num;
    };

    // Funkcja do obliczania średniej z wszystkich ocen (wykluczając tylko prawdziwą ocenę końcową)
    const computeFinalAverage = (allGrades) => {
        const nums = [];
        allGrades.forEach((g) => {
            // Wyklucz tylko prawdziwą ocenę końcową (z student_grades), nie egzamin końcowy
            // Ocena końcowa ma grade_type = 'Ocena końcowa' i exam_type = NULL
            if (g.grade_type === 'Ocena końcowa' && !g.exam_type) {
                return;
            }
            if (isNumericGrade(g.grade)) {
                const num = toNumber(g.grade);
                if (num !== null && Number.isFinite(num)) {
                    nums.push(num);
                }
            }
        });

        if (!nums.length) return null;

        const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        return avg;
    };

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/lecturer/grades/studentModules`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(response => setModules(response.data))
        .catch(error => console.error('Error fetching modules:', error))
        .finally(() => setLoadingModules(false));
    }, []);

    useEffect(() => {
    if (!selectedModule) {
        setGrades([]);
        return;
    }

    const token = localStorage.getItem("token");
        if (!token) return;

        setLoadingGrades(true);

        axios.get(`${process.env.REACT_APP_API_URL}/lecturer/grades/studentGrades`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { 
                module_id: selectedModule
            }
        })
        .then(response => setGrades(response.data))
        .catch(error => console.error('Error fetching grades:', error))
        .finally(() => setLoadingGrades(false));
    }, [selectedModule]);

    const handleShowStats = () => {
      if (!showStudentStats && !studentStats) {
        const token = localStorage.getItem("token");
        if (!token) return;

        setLoadingStudentStats(true);

        axios.get(`${process.env.REACT_APP_API_URL}/lecturer/grades/studentStats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(response => setStudentStats(response.data))
        .catch(error => console.error('Error fetching student stats:', error))
        .finally(() => setLoadingStudentStats(false));

        console.log(studentStats);
      }
      setShowStudentStats(prev => !prev);
    };

    return (
        <div className="student-dashboard-panel">
            {/* Sidebar */}
            <StudentSidebar />
            {/* Main Content */}
            <div className='main-content'>
                {/* Header */}
                <div className='header'>
                    <h1 className='header-title'>Moje Oceny</h1>
                </div>
                {/* Dashboard Content */}
                <div className="dashboard">
                    <div className='student-header'>
                        <h1 className='student-title'>Moje Oceny</h1>
                        <p className="student-subheading">Przeglądaj oceny i statystyki</p>
                    </div>
                    {/* Controls */}
                    <div className='student-controls'>
                        {loadModules ? (
                            <p>Ładowanie modułów...</p>
                        ) : (
                            <select className='student-module-select' onChange={(e) => setSelectedModule(e.target.value)}>
                                <option value="">Wybierz przedmiot</option>
                                {modules.map(module => (
                                    <option key={module.module_id} value={module.module_id}>
                                        {module.module_name}
                                    </option>
                                ))}
                            </select>
                        )}
                        <div style={{ position: 'relative' }}>
                            <Search size={20} className='search-icon-grades' />
                            <input
                                type='text'
                                placeholder='Szukaj oceny'
                                className='search-input-grades'
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className='show-stat-grades' onClick={handleShowStats}>
                            <BarChart2 size={20} style={{ marginRight: '0.5rem' }} />
                            <span>{showStudentStats ? 'Ukryj Statystyki' : 'Pokaż Statystyki'}</span>
                        </button>
                    </div>
                    {/* Student Stats */}
                    {loadingStudentStats && <p>Ładowanie statystyk...</p>}
                    {studentStats && showStudentStats && (
                        <div className='student-stats'>
                            <div className='stat-item-grades'>
                                <h3 style={{ margin: 0 }}>Średnia Ocena:</h3>
                                <p className='stat-value avg'>{studentStats.avg_grade || 0}</p>
                            </div>
                            <div className='stat-item-grades'>
                                <h3 style={{ margin: 0 }}>Zaliczone</h3>
                                <p className='stat-value avg'>{ (Number(studentStats.submitted_tasks) + Number(studentStats.passed_tasks)) || 0 }</p>
                            </div>
                            <div className='stat-item-grades'>
                                <h3 style={{ margin: 0 }}>W trakcie</h3>
                                <p className='stat-value progress'>{studentStats.in_progress_tasks || 0}</p>
                            </div>
                        </div>
                    )}
                    {/* Final Grade Display */}
                    {loadingGrades ? null : grades.length > 0 && (() => {
                        // Szukaj tylko prawdziwej oceny końcowej (z student_grades), nie egzaminu końcowego
                        // Ocena końcowa ma grade_type = 'Ocena końcowa' i exam_type = NULL
                        // Egzamin końcowy ma grade_type = 'Egzamin końcowy' i exam_type = 'FINAL'
                        const finalGrade = grades.find(g => 
                            g.grade_type === 'Ocena końcowa' && !g.exam_type
                        );
                        
                        // Oblicz średnią z wszystkich ocen (wykluczając ocenę końcową)
                        const averageGrade = computeFinalAverage(grades);
                        
                        // Jeśli nie ma oceny końcowej, wyświetl proponowaną średnią na szaro
                        if (!finalGrade && averageGrade !== null) {
                            return (
                                <div className='final-grade-display'>
                                    <div className='final-grade-card final-grade-proposed'>
                                        <div className='final-grade-label'>Proponowana ocena końcowa</div>
                                        <div className='final-grade-value final-grade-proposed-value'>
                                            {averageGrade.toFixed(2)}
                                        </div>
                                        <div className='final-grade-date'>Średnia z wszystkich ocen</div>
                                    </div>
                                </div>
                            );
                        }
                        
                        // Jeśli jest ocena końcowa, wyświetl ją normalnie
                        if (finalGrade) {
                            return (
                                <div className='final-grade-display'>
                                    <div 
                                        className='final-grade-card final-grade-clickable'
                                        onClick={() => handleOpenModal(finalGrade)}
                                        title="Kliknij, aby zobaczyć szczegóły"
                                    >
                                        <div className='final-grade-label'>Ocena końcowa</div>
                                        <div className={`final-grade-value ${getGradeClass(finalGrade.grade)}`}>
                                            {showGrade(finalGrade)}
                                        </div>
                                        {finalGrade.passed && (
                                            <div className={`final-grade-status ${getStatusClass(finalGrade.passed)}`}>
                                                {finalGrade.passed}
                                            </div>
                                        )}
                                        {finalGrade.grade_date && (
                                            <div className='final-grade-date'>Data: {finalGrade.grade_date}</div>
                                        )}
                                    </div>
                                </div>
                            );
                        }
                        
                        return null;
                    })()}
                    {/* Grades list */}
                    <div className='grades-list-container'>
                        {loadingGrades ? (
                            <p>Ładowanie ocen...</p>
                        ) : (
                            grades.length > 0 ? (() => {
                                // Grupuj egzaminy według exam_type
                                const groupedGrades = [];
                                const examGroups = {};

                                grades.forEach((grade, idx) => {
                                    // Pomiń tylko prawdziwą ocenę końcową (z student_grades) - będzie wyświetlana osobno
                                    // Nie pomijaj egzaminu końcowego (FINAL exam) - powinien być w tabeli
                                    if (grade.grade_type === 'Ocena końcowa' && !grade.exam_type) {
                                        return;
                                    }
                                    
                                    // Sprawdź czy to egzamin z exam_type
                                    // Upewnijmy się, że sprawdzamy zarówno exam_type jak i grade_type zawierające "Egzamin"
                                    if (grade.exam_type && (grade.grade_type?.includes('Egzamin') || grade.grade_type === 'Egzamin końcowy' || grade.grade_type === 'Egzamin poprawkowy' || grade.grade_type === 'Egzamin śródsemestralny')) {
                                        const examKey = grade.exam_type;
                                        if (!examGroups[examKey]) {
                                            examGroups[examKey] = [];
                                        }
                                        examGroups[examKey].push({ ...grade, originalIdx: idx });
                                    } else if (grade.grade_type?.includes('Egzamin') && !grade.exam_type) {
                                        // Jeśli to egzamin bez exam_type, dodaj jako zwykłą ocenę
                                        groupedGrades.push({ ...grade, originalIdx: idx, isExam: false });
                                    } else {
                                        // Inne typy ocen dodajemy normalnie
                                        groupedGrades.push({ ...grade, originalIdx: idx, isExam: false });
                                    }
                                });

                                // Przetwórz grupy egzaminów - dodaj tylko jedną linię z wszystkimi próbami
                                // Sortuj typy egzaminów: FINAL, MIDTERM, RESIT (w tej kolejności)
                                const examTypeOrder = { 'FINAL': 1, 'MIDTERM': 2, 'RESIT': 3 };
                                
                                Object.keys(examGroups)
                                    .sort((a, b) => {
                                        const orderA = examTypeOrder[a] || 99;
                                        const orderB = examTypeOrder[b] || 99;
                                        if (orderA !== orderB) return orderA - orderB;
                                        // Jeśli ten sam typ, sortuj według najniższego originalIdx
                                        const minIdxA = Math.min(...examGroups[a].map(e => e.originalIdx));
                                        const minIdxB = Math.min(...examGroups[b].map(e => e.originalIdx));
                                        return minIdxA - minIdxB;
                                    })
                                    .forEach(examType => {
                                        const exams = examGroups[examType];
                                        
                                        // Sortuj według attempt (rosnąco)
                                        exams.sort((a, b) => {
                                            const attemptA = a.attempt || 1;
                                            const attemptB = b.attempt || 1;
                                            if (attemptA !== attemptB) return attemptA - attemptB;
                                            const dateA = String(a.grade_date || "");
                                            const dateB = String(b.grade_date || "");
                                            return dateA.localeCompare(dateB);
                                        });
                                        
                                        // Użyj najnowszego egzaminu jako głównego (najwyższa próba)
                                        const latestExam = exams[exams.length - 1];
                                        groupedGrades.push({
                                            ...latestExam,
                                            isExam: true,
                                            allAttempts: exams,
                                            originalIdx: latestExam.originalIdx
                                        });
                                    });

                                // Sortuj: najpierw egzaminy (według typu), potem inne oceny (według oryginalnego indeksu)
                                groupedGrades.sort((a, b) => {
                                    // Jeśli oba są egzaminami, sortuj według typu egzaminu
                                    if (a.isExam && b.isExam) {
                                        const orderA = examTypeOrder[a.exam_type] || 99;
                                        const orderB = examTypeOrder[b.exam_type] || 99;
                                        if (orderA !== orderB) return orderA - orderB;
                                        // Jeśli ten sam typ, zachowaj kolejność
                                        return 0;
                                    }
                                    // Jeśli tylko jeden jest egzaminem, egzaminy idą na początku
                                    if (a.isExam) return -1;
                                    if (b.isExam) return 1;
                                    // W przeciwnym razie sortuj według oryginalnego indeksu
                                    return a.originalIdx - b.originalIdx;
                                });

                                return (
                                    <>
                                        <div className='grades-list-header'>
                                            <span>Typ oceny</span>
                                            <span>Nazwa</span>
                                            <span>Ocena</span>
                                            <span>Status</span>
                                            <span>Data</span>
                                            <span>Wykładowca</span>
                                            <span>Akcje</span>
                                        </div>
                                        <div className='grades-list-body'>
                                            {groupedGrades.map((grade, idx) => {
                                                // Determine chip color based on grade type
                                                const getChipClass = () => {
                                                    if (grade.grade_type?.includes('Egzamin')) return 'sg-chip-exam';
                                                    if (grade.grade_type === 'Zadanie') return 'sg-chip-assignment';
                                                    if (grade.grade_type === 'Ocena końcowa') return 'sg-chip-final';
                                                    return 'sg-chip-activity';
                                                };
                                                
                                                // Dla egzaminów z wieloma próbami, wyświetl wszystkie próby
                                                const renderGrade = () => {
                                                    if (grade.isExam && grade.allAttempts && grade.allAttempts.length > 1) {
                                                        const latestAttempt = Math.max(...grade.allAttempts.map(e => e.attempt || 1));
                                                        return (
                                                            <span className="sg-exam-attempts">
                                                                {grade.allAttempts.map((exam, examIdx) => {
                                                                    const isLatest = (exam.attempt || 1) === latestAttempt;
                                                                    return (
                                                                        <span 
                                                                            key={examIdx}
                                                                            className={`sg-exam-attempt student-grade ${getGradeClass(exam.grade)} ${isLatest ? '' : 'sg-exam-strikethrough'}`}
                                                                        >
                                                                            {showGrade(exam)}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </span>
                                                        );
                                                    }
                                                    return <span className={`student-grade ${getGradeClass(grade.grade)}`}>
                                                        {showGrade(grade)}
                                                    </span>;
                                                };
                                                
                                                return (
                                                    <div key={idx} className='grades-list-row' onClick={() => handleOpenModal(grade)}>
                                                        <span className={`sg-chip ${getChipClass()}`}>{grade.grade_type}</span>
                                                        <span className='sg-grade-name'>{grade.grade_name}</span>
                                                        {renderGrade()}
                                                        <span className={`student-passed ${getStatusClass(grade.passed)}`}>
                                                            {grade.passed}
                                                        </span>
                                                        <span className='sg-date'>{grade.grade_date}</span>
                                                        <span className='sg-lecturer'>{grade.lecturer_name || "—"}</span>
                                                        <button 
                                                            className='sg-details-btn' 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenModal(grade);
                                                            }}
                                                        >
                                                            Szczegóły
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                );
                            })() : (
                                <p style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>Brak ocen dla wybranego modułu</p>
                            )
                        )}
                    </div>
                    
                    {/* Modal */}
                    {isModalOpen && selectedGradeDetails && (
                        <div className="modal-overlay" onClick={handleCloseModal}>
                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                <h2 
                                  className={`grade-title-details ${
                                    selectedGradeDetails.grade_type?.includes('Egzamin') ? 'exam-style' : 
                                    selectedGradeDetails.grade_type === 'Zadanie' ? 'assignment-style' :
                                    selectedGradeDetails.grade_type === 'Ocena końcowa' ? 'final-style' :
                                    'activity-style'
                                  }`}
                                >
                                  {selectedGradeDetails.grade_name}
                                </h2>
                                <p><span className='grade-label-details'>Typ oceny:</span> {selectedGradeDetails.grade_type}</p>
                                <p>
                                    <span className='grade-label-details'>Ocena:</span> 
                                    {selectedGradeDetails.allAttempts && selectedGradeDetails.allAttempts.length > 1 ? (
                                        <span className="sg-exam-attempts-modal">
                                            {selectedGradeDetails.allAttempts.map((exam, examIdx) => {
                                                const latestAttempt = Math.max(...selectedGradeDetails.allAttempts.map(e => e.attempt || 1));
                                                const isLatest = (exam.attempt || 1) === latestAttempt;
                                                return (
                                                    <span 
                                                        key={examIdx}
                                                        className={`sg-exam-attempt-modal ${getGradeClass(exam.grade)} ${isLatest ? '' : 'sg-exam-strikethrough'}`}
                                                    >
                                                        {showGrade(exam)}
                                                    </span>
                                                );
                                            })}
                                        </span>
                                    ) : (
                                        <span className={`student-grade ${getGradeClass(selectedGradeDetails.grade)}`}>{showGrade(selectedGradeDetails)}</span>
                                    )}
                                </p>
                                
                                {/* Exam-specific details */}
                                {selectedGradeDetails.exam_type && (
                                    <>
                                        <p><span className='grade-label-details'>Typ egzaminu:</span> {
                                            selectedGradeDetails.exam_type === 'FINAL' ? 'Egzamin końcowy' :
                                            selectedGradeDetails.exam_type === 'MIDTERM' ? 'Egzamin śródsemestralny' :
                                            selectedGradeDetails.exam_type === 'RESIT' ? 'Egzamin poprawkowy' :
                                            selectedGradeDetails.exam_type
                                        }</p>
                                        {selectedGradeDetails.allAttempts && selectedGradeDetails.allAttempts.length > 1 ? (
                                            <p>
                                                <span className='grade-label-details'>Próby:</span> 
                                                {selectedGradeDetails.allAttempts.map((exam, idx) => (
                                                    <span key={idx} style={{ marginLeft: '0.5rem' }}>
                                                        Próba {exam.attempt || 1}: {showGrade(exam)} 
                                                        {exam.grade_date && ` (${exam.grade_date})`}
                                                        {idx < selectedGradeDetails.allAttempts.length - 1 ? ', ' : ''}
                                                    </span>
                                                ))}
                                            </p>
                                        ) : selectedGradeDetails.attempt && selectedGradeDetails.attempt > 1 ? (
                                            <p><span className='grade-label-details'>Próba:</span> {selectedGradeDetails.attempt}</p>
                                        ) : null}
                                    </>
                                )}
                                
                                {/* Activity-specific details */}
                                {selectedGradeDetails.activity_type && (
                                    <>
                                        {selectedGradeDetails.weight && selectedGradeDetails.weight !== 1.0 && (
                                            <p><span className='grade-label-details'>Waga:</span> {parseFloat(selectedGradeDetails.weight).toFixed(2)}</p>
                                        )}
                                        {selectedGradeDetails.description && (
                                            <p><span className='grade-label-details'>Opis:</span> {selectedGradeDetails.description}</p>
                                        )}
                                    </>
                                )}
                                
                                {selectedGradeDetails.lecturer_name && (
                                    <p><span className='grade-label-details'>Wykładowca:</span> {selectedGradeDetails.lecturer_name}</p>
                                )}
                                <p><span className='grade-label-details'>Data:</span> {selectedGradeDetails.grade_date}</p>
                                {selectedGradeDetails.grade_time && (
                                    <p><span className='grade-label-details'>Godzina:</span> {selectedGradeDetails.grade_time}</p>
                                )}
                                <p><span className='grade-label-details'>Status:</span> <span className={`student-passed ${getStatusClass(selectedGradeDetails.passed)}`}>{selectedGradeDetails.passed}</span></p>
                                
                                {/* Assignment feedback */}
                                {selectedGradeDetails.grade_type === 'Zadanie' && selectedGradeDetails.feedback && (
                                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', borderLeft: '3px solid #16a34a' }}>
                                        <strong>Feedback:</strong> {selectedGradeDetails.feedback}
                                    </div>
                                )}
                                
                                <button className='btn button-secondary' onClick={handleCloseModal} style={{ marginTop: '1rem' }}>Zamknij</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}